import { useContext, useState } from 'react';

import {
  Button,
  copyTextToClipboard,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableRow
} from '@@/ui/primitives';
import { SettingsIcon, MoreVerticalIcon, ClipboardCopyIcon, PlusIcon, CopySlashIcon } from 'lucide-react';
import clsx from 'clsx';
import { EntityRecord } from '../../../../api/api-entities';
import { AuthContext } from '@@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { CreateEntityModal } from '../create-entity-modal';
import { EntityRow } from './row';
import { Breadcrumbs } from './breadcrumbs';
import { EntityExportDialog } from './import-export/export.modal';
import { EntityImportDialog } from './import-export/import.modal';
import { useGetEntity, useListEntities } from '../../../../hooks/use-entities';
import { Spinner } from '@@/ui/components';

export function EntityBrowser({ entityId }: { entityId: string }) {
  const { data: entities, error, isLoading } = useListEntities({ parentId: entityId });
  const { data: entity, isLoading: isEntityLoading } = useGetEntity(entityId, {
    enabled: entityId !== 'root'
  });

  const [createEntityModalOpen, setCreateEntityModalOpen] = useState(false);
  const { getPathWithInstanceAlias } = useContext(AuthContext);
  const navigate = useNavigate();

  const handleNavigate = (entity: EntityRecord) => {
    navigate(getPathWithInstanceAlias(`/portal/dfc/browser/${entity.entityId}`));
  };
  const handleSettings = () => {
    navigate(getPathWithInstanceAlias(`/portal/dfc/view/${entityId}`));
  };

  const handleCreateEntity = () => {
    setCreateEntityModalOpen(true);
  };

  const isRoot = entityId === 'root';

  return (
    <div className={clsx('overflow-hidden border border-slate-300 rounded-md h-auto w-90')}>
      <Breadcrumbs currentEntity={entity} />
      <div className={clsx('flex items-start justify-between', 'px-3 py-3', 'bg-slate-50')}>
        <div className='flex flex-col gap-1 flex-1'>
          <div className='flex justify-between items-center mb-2'>
            <div className='flex gap-2 items-center'>
              <span className='text-slate-950 font-semibold truncate max-w-[16ch] leading-snug'>
                {isEntityLoading && <Skeleton className='h-4 w-32 bg-slate-300' />}
                {isRoot ? 'Home' : entity ? entity.name : null}
              </span>
            </div>
            <div className='flex flex-row gap-1 items-end'>
              <Button
                variant='outline'
                size='sm'
                disabled={isEntityLoading}
                className='text-sm! shadow-none! font-medium! rounded-full px-3! leading-snug'
                onClick={handleCreateEntity}
                title={isRoot ? 'Create Entity' : 'Create Sub-Entity'}
              >
                <PlusIcon className='size-4 mt-px -ml-px' />
                Entity
              </Button>

              <DropdownMenu modal={false}>
                <DropdownMenuTrigger disabled={isEntityLoading} asChild>
                  <Button
                    variant='ghost'
                    size='icon'
                    className='text-sm! font-medium! rounded-full size-8'
                    disabled={isEntityLoading}
                  >
                    <MoreVerticalIcon className='size-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end' className='font-sans! font-[450]!'>
                  {!isRoot && (
                    <DropdownMenuItem onClick={handleSettings}>
                      <SettingsIcon />
                      Settings
                    </DropdownMenuItem>
                  )}
                  {entity && (
                    <>
                      <DropdownMenuItem onClick={() => copyTextToClipboard(entity.entityId)}>
                        <ClipboardCopyIcon />
                        Copy ID
                      </DropdownMenuItem>

                      <DropdownMenuItem onClick={() => copyTextToClipboard(entity.path)}>
                        <CopySlashIcon />
                        Copy Path
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuGroup className='flex flex-col'>
                    <DropdownMenuLabel className='text-muted-foreground'>Migrate</DropdownMenuLabel>
                    <EntityExportDialog baseEntityId={entityId} />
                    <EntityImportDialog baseEntityId={entityId} />
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          <span className='text-slate-600 text-sm leading-snug min-h-6 flex items-center'>
            {isRoot && 'You are in the root. This is where you can set up your top-level Entities.'}
            {!isRoot && entity && entity.description}
            {isEntityLoading && <Skeleton className='h-4 w-48 bg-slate-300' />}
          </span>
        </div>
      </div>

      <div className={clsx('flex items-center py-1 pl-3 pr-2 justify-between bg-slate-50 border-b border-slate-300')}>
        <div className={clsx('flex items-center gap-3 w-full')}>
          <span className='text-xs font-medium text-slate-950'>
            {isRoot && <span>Entities</span>}
            {!isRoot && <span>Sub-Entities</span>}
          </span>
          {entities && (
            <span className='text-xs text-slate-500 border-slate-300 ml-auto'>
              {entities.length} {entities.length === 1 ? 'entity' : 'entities'}
            </span>
          )}
        </div>
      </div>
      <Table parentClassname='h-[calc(100vh-20rem)] overflow-x-auto'>
        {!isLoading && entities && (
          <>
            <TableBody>
              {entities.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className='text-center py-4 text-neutral-500'>
                    There are no Sub-Entities
                  </TableCell>
                </TableRow>
              )}
              {entities.map((item) => (
                <EntityRow key={item.entityId} item={item} onNavigate={handleNavigate} />
              ))}
            </TableBody>
          </>
        )}
        {isLoading && (
          <TableBody>
            <TableRow>
              <TableCell colSpan={7} className='flex items-center py-4 justify-center'>
                <Spinner />
              </TableCell>
            </TableRow>
          </TableBody>
        )}
        {error && (
          <TableBody>
            <TableRow>
              <TableCell colSpan={7} className='text-center py-4 text-red-600'>
                Error loading entities: {(error as Error).message}
              </TableCell>
            </TableRow>
          </TableBody>
        )}
      </Table>
      <CreateEntityModal
        open={createEntityModalOpen}
        onOpenChange={setCreateEntityModalOpen}
        parentId={entityId}
        isRoot={isRoot}
      />
    </div>
  );
}
